import React, {useCallback, useMemo} from 'react';
import {useAppContext} from 'contexts';
import {jobId} from 'shared/helpers';
import type {FieldType, TableOptionType, TableRowType} from 'shared/types';
import {
    DATE_FIELD_PRIORITY,
    JOB_STATUS_LABELS,
    formatEnteredDate,
    formatFieldValue,
    getStatusVariant,
} from './helpers';
import {JobCardActions} from './JobCardActions';

export const JobCard = ({
    row,
    fields,
    buttonsConfig,
}: {
    row: TableRowType;
    fields: FieldType[];
    buttonsConfig?: TableOptionType;
}) => {
    const {userProfile} = useAppContext();

    const getField = useCallback(
        (name: string) => fields.find((field) => field.fieldName === name),
        [fields]
    );

    const priceField = getField('cost') ?? getField('preliminaryCost');
    const updatedField = useMemo(
        () => DATE_FIELD_PRIORITY.map((name) => getField(name)).find(Boolean),
        [getField]
    );

    const statusInfo = useMemo(() => {
        const status = Number(row.status);
        const customStatus = userProfile?.jobStatuses?.find(
            (item) => item.status === status
        );
        const label =
            customStatus?.custom_label || JOB_STATUS_LABELS.get(status) || '-';

        return {label, variant: getStatusVariant(status)};
    }, [row.status, userProfile?.jobStatuses]);

    const enteredValue = formatEnteredDate(
        row.dateEnteredDateTime ?? row.dateEntered
    );

    return (
        <div className="job-card">
            <div className="job-card-header">
                <div className="job-card-title">
                    <div className="job-card-name">{row.name}</div>
                    <div className="job-card-number">Job #{jobId(row.id)}</div>
                </div>
                <span
                    className={`job-card-status job-card-status-${statusInfo.variant}`}>
                    {statusInfo.label}
                </span>
            </div>

            <div className="job-card-body">
                <div className="job-card-stats">
                    <div className="job-card-stat">
                        <span className="job-card-stat-label">
                            Cost (incl. Tax)
                        </span>
                        <span className="job-card-stat-value">
                            {priceField
                                ? formatFieldValue(priceField, row)
                                : '-'}
                        </span>
                    </div>
                    <div className="job-card-stat">
                        <span className="job-card-stat-label">
                            Rooms / Products
                        </span>
                        <span
                            className="job-card-stat-value"
                            style={{marginLeft: '3px'}}>
                            {String(row.roomCount ?? '-')} /{' '}
                            {String(row.productCount ?? '-')}
                        </span>
                    </div>
                    <div className="job-card-stat">
                        <span className="job-card-stat-label">
                            Date Entered
                        </span>
                        <span className="job-card-stat-value">
                            {enteredValue}
                        </span>
                    </div>
                    <div className="job-card-stat">
                        <span className="job-card-stat-label">
                            Last updated
                        </span>
                        <span className="job-card-stat-value">
                            {updatedField
                                ? String(formatFieldValue(updatedField, row))
                                : '-'}
                        </span>
                    </div>
                </div>
            </div>

            {buttonsConfig ? (
                <JobCardActions buttons={buttonsConfig.buttons} row={row} />
            ) : null}
        </div>
    );
};
